import * as PIXI from "pixi.js"
import Model from "../model/model"
import { Idea } from "../model/types"
import { defaultIdeaColourResolver, type IdeaColourResolver } from "./idea_colour"
import { type PixiLayers } from "./pixi_scene"
import { createOccupiedGridCellResolver, renderIdeaToGraphic } from "./renderers"

export default class IdeaGraphicsRenderer {
  private app: PIXI.Application
  private layers: PixiLayers
  private idea_colour_resolver: () => IdeaColourResolver

  constructor(
    app: PIXI.Application,
    layers: PixiLayers,
    idea_colour_resolver: () => IdeaColourResolver = () => defaultIdeaColourResolver,
  ) {
    this.app = app
    this.layers = layers
    this.idea_colour_resolver = idea_colour_resolver
  }

  render_fresh_ideas(model: Model, hiding_ideas: Idea[] = []): PIXI.Graphics {
    const { ideas_graphic, connections_graphic } = this.reset_idea_graphics()
    const hiddenIdeaIds = new Set(hiding_ideas.map((idea) => idea.id))
    const visibleIdeas = Array.from(model.store.ideas.values()).filter(
      (idea) => !hiddenIdeaIds.has(idea.id),
    )
    const isGridCellOccupied = createOccupiedGridCellResolver(visibleIdeas)

    visibleIdeas.forEach((idea) => {
      renderIdeaToGraphic(
        ideas_graphic,
        connections_graphic,
        idea,
        model,
        0,
        0,
        this.idea_colour_resolver(),
        isGridCellOccupied,
      )
    })

    return ideas_graphic
  }

  private reset_idea_graphics(): {
    ideas_graphic: PIXI.Graphics
    connections_graphic: PIXI.Graphics
  } {
    if (this.layers.ideas == null) {
      this.layers.connections = new PIXI.Graphics()
      this.app.stage.addChildAt(
        this.layers.connections,
        this.app.stage.children.indexOf(this.layers.hover),
      )
      this.layers.ideas = new PIXI.Graphics()
      this.app.stage.addChildAt(
        this.layers.ideas,
        this.app.stage.children.indexOf(this.layers.hover),
      )
      this.layers.ideas.interactive = true
      this.layers.ideas.cursor = "pointer"
    } else {
      this.layers.ideas.clear()
      if (this.layers.connections) this.layers.connections.clear()
    }

    if (this.layers.connections == null)
      throw new Error("connections layer missing after resetting idea graphics")

    return {
      ideas_graphic: this.layers.ideas,
      connections_graphic: this.layers.connections,
    }
  }
}
